import { useState } from 'react';
import { motion } from 'motion/react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { getLinkToFreshSignup } from '@farmersdog/tosa/src/utils/getLinkToSignupFlow';
import { PATH_SIGNUP_ME } from '@farmersdog/constants/paths';
import {
  Flex,
  GridItem,
  Nowrap,
  Picture,
  Text,
  useViewport,
} from '@farmersdog/corgi-x';
import { ExtendedButton, ExtendedText } from '../../../../components';
import { CtaTrackerV2, HomepageRedesignSections } from '../../../CtaTrackerV2';
import {
  bowlPackDesktopSources,
  bowlPackMobileSources,
  GreenCheck,
  RedX,
} from './assets';
import ArrowJson from './assets/arrow.json';
import styles from './OlsVetsBowlPackSection.module.css';

const ourFoodItems = [
  'Real meat and veggies',
  'Human-grade standards',
  'Gently cooked',
  'Pre-portioned packs',
];

const theirFoodItems = [
  'Low-quality ingredients',
  'Feed-grade standards',
  'Extremely processed',
  'Vague scoops',
];

export const OlsVetsBowlPackSection = () => {
  const viewport = useViewport();
  const isMobile = !viewport.lg;
  const linkToFreshSignup = getLinkToFreshSignup(PATH_SIGNUP_ME);
  const [scrolledArrowIntoView, setScrolledArrowIntoView] = useState(false);
  const shouldTriggerArrowDrawIn = isMobile && scrolledArrowIntoView;

  return (
    <div className={styles.wrapper}>
      <div className={styles.headingContainer}>
        <ExtendedText
          variant={isMobile ? 'heading-28' : 'heading-36'}
          as="h2"
          id="bowl-pack-section-heading"
          weight="black"
        >
          You shouldn’t be the only <Nowrap>one eating healthy</Nowrap>
        </ExtendedText>
      </div>
      <div className={styles.subheadingContainer}>
        <Text
          variant={isMobile ? 'heading-16' : 'heading-22'}
          as="p"
          color="Kale3"
        >
          We love our pets and care about their health. {isMobile && <br />}
          That’s why we started The Farmer’s Dog:
          <br />
          to make a better kind of dog food.
        </Text>
      </div>
      <Flex
        direction={isMobile ? 'column' : 'row-reverse'}
        justify={isMobile ? 'center' : 'flex-end'}
        alignItems="center"
      >
        <div className={styles.imageContainer}>
          <Picture
            sources={[...bowlPackMobileSources, ...bowlPackDesktopSources]}
            alt="Turkey Bowl"
            loading="lazy"
            className={styles.bowlImage}
          />
        </div>
        <motion.div
          viewport={{ once: true, amount: 0.2 }}
          onViewportEnter={() => {
            setScrolledArrowIntoView(true);
          }}
        >
          <Flex
            direction="column"
            gap={isMobile ? 'md' : 'xl'}
            alignItems={isMobile ? 'center' : 'start'}
          >
            <Flex
              className={styles.comparison}
              justify="center"
              direction={isMobile ? 'row' : 'row-reverse'}
            >
              <GridItem xs={6} className={styles.ourFood}>
                {shouldTriggerArrowDrawIn && (
                  <DotLottieReact
                    aria-hidden="true"
                    autoplay
                    loop={false}
                    data={ArrowJson}
                    className={styles.arrow}
                  />
                )}
                <ExtendedText
                  variant={isMobile ? 'heading-15' : 'heading-18'}
                  as="h3"
                  weight="bold"
                  color="Kale3"
                >
                  Our Food
                </ExtendedText>
                <ul>
                  {ourFoodItems.map(item => (
                    <li key={item}>
                      <GreenCheck
                        height={isMobile ? 14 : 26}
                        width={isMobile ? 14 : 20}
                      />
                      <ExtendedText
                        variant={isMobile ? 'heading-13' : 'heading-18'}
                        weight="semibold"
                        color="Kale2"
                      >
                        {item}
                      </ExtendedText>
                    </li>
                  ))}
                </ul>
              </GridItem>
              <GridItem xs={6} className={styles.theirFood}>
                <ExtendedText
                  variant={isMobile ? 'heading-15' : 'heading-18'}
                  as="h3"
                  weight="semibold"
                  color="Kale3"
                >
                  Their Food
                </ExtendedText>
                <ul>
                  {theirFoodItems.map(item => (
                    <li key={item}>
                      <RedX
                        height={isMobile ? 14 : 26}
                        width={isMobile ? 14 : 20}
                      />
                      <ExtendedText
                        variant={isMobile ? 'heading-13' : 'heading-18'}
                        color="Kale3"
                      >
                        {item}
                      </ExtendedText>
                    </li>
                  ))}
                </ul>
              </GridItem>
            </Flex>
            <div className={styles.ctaContainer}>
              <CtaTrackerV2
                type="select"
                moduleLevel={1}
                moduleName={HomepageRedesignSections.BowlPack}
              >
                <ExtendedButton
                  variant="solid-kale-2"
                  type="link"
                  to={linkToFreshSignup}
                >
                  Make The Switch
                </ExtendedButton>
              </CtaTrackerV2>
            </div>
          </Flex>
        </motion.div>
      </Flex>
    </div>
  );
};
